import { request as httpRequest } from './http-library.js';
import publicConfig from '../public-config.js';

const calendarHost = publicConfig.external_address;

const Calendar = {
  /**
   * Read booking availability by month and year
   *
   * @function
   * @name read
   * @param {string}   - query
   * @param {string} - token
   * @param {function}  - callback
   */

  readAnonymous: (query, callback) => {
    const params = {
      host: calendarHost,
      method: 'GET',
      path: `/v1/calendar/${query}`
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  readAnonymousV2: (listingId, callback) => {
    const params = {
      host: calendarHost,
      method: 'GET',
      path: `/v2/spaces-v2/listings/${listingId}/calendar-pricing`
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  readCalendar: (query, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'GET',
      path: `/v1/calendar/${query}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  readCalendarV2: (listingId, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'GET',
      path: `/v2/spaces-v2/listings/${listingId}/calendar-pricing`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  createCalendarPricing: (payload, listingId, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'POST',
      path: `/v2/spaces-v2/listings/${listingId}/calendar-pricing`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    }, payload);
  },

  postBlockDay: (payload, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'POST',
      path: '/v1/calendar/events/',
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    }, payload);
  },

  deleteCalendar: (query, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'DELETE',
      path: `/v1/calendar/${query}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  deleteCalendarPricing: (groupId, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'DELETE',
      path: `/v2/spaces-v2/listings/calendar-pricing/group/${groupId}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  deleteBlockDay: (groupId, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'DELETE',
      path: `/v1/calendar/events/group/${groupId}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  getOpHours: (spaceId, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'GET',
      path: `/v1/calendar/ophours/space/${spaceId}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  getBufferTimeSetting: (spaceId, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'GET',
      path: `/v1/calendar/buffertimes/space/${spaceId}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  setBufferTimeSetting: (spaceId, token, payload, callback) => {
    const params = {
      host: calendarHost,
      method: 'PUT',
      path: `/v1/calendar/buffertimes/space/${spaceId}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    }, payload);
  },

  postOpHours: (spaceId, token, payload, callback) => {
    const params = {
      host: calendarHost,
      method: 'POST',
      path: `/v1/calendar/ophours/space/${spaceId}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    }, payload);
  },

  importCalendarLink: (spaceId, token, importUrl, calendarName, callback) => {
    const postBody = {
      source_url: importUrl,
      calendar_name: calendarName
    };
    const path = `/v1/calendar/cronofy/space/${spaceId}/ics/authorize`;
    const params = {
      host: calendarHost,
      method: 'POST',
      path,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      if (callback) callback(res);
    }, postBody);
  },

  fetchCalendarSurveyData: (spaceId, token, callback) => {
    const params = {
      host: calendarHost,
      method: 'GET',
      path: `/v1/calendar/survey/${spaceId}/data`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    });
  },

  updateCalendarSyncReasons: (spaceId, token, payload, callback) => {
    const params = {
      host: calendarHost,
      method: 'PUT',
      path: `/v1/calendar/survey/${spaceId}/sync/reasons`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    }, payload);
  },

  updateCalendarSyncReminder: (spaceId, token, payload, callback) => {
    const params = {
      host: calendarHost,
      method: 'PUT',
      path: `/v1/calendar/survey/${spaceId}/sync/reminders`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    httpRequest(params, (res) => {
      callback(res);
    }, payload);
  }
};

export default Calendar;

// Named exports for ES module consumers
export const {
  readAnonymous,
  readAnonymousV2,
  readCalendar,
  readCalendarV2,
  createCalendarPricing,
  postBlockDay,
  deleteCalendar,
  deleteCalendarPricing,
  deleteBlockDay,
  getOpHours,
  getBufferTimeSetting,
  setBufferTimeSetting,
  postOpHours,
  importCalendarLink,
  fetchCalendarSurveyData,
  updateCalendarSyncReasons,
  updateCalendarSyncReminder
} = Calendar;
